import { useImmutableSetState } from '@cosuno/react-hooks';
import { useCallback, useMemo, useRef, useState } from 'react';

export interface DownloadPreparationManagerMethods {
  startDownloadPreparation: (id: string) => void;
  stopDownloadPreparation: (id: string) => void;
  getIsPreparingDownload: (id: string) => boolean;
  status: 'ready' | 'preparing' | 'done';
  internalApiUrl: string;
}

export const useDownloadPreparationManager = ({
  internalApiUrl,
}: {
  internalApiUrl: string;
}): DownloadPreparationManagerMethods => {
  const {
    addItem: startDownloadPreparation,
    removeItem,
    hasItem: getIsPreparingDownload,
    itemsCount,
  } = useImmutableSetState<string>();

  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const [isDoneAnyDownload, setIsDoneAnyDownload] = useState(false);

  const stopDownloadPreparation = useCallback(
    (item: string) => {
      removeItem(item);
      setIsDoneAnyDownload(true);
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      timeout.current = setTimeout(() => {
        setIsDoneAnyDownload(false);
        timeout.current = undefined;
      }, 3000);
    },
    [removeItem],
  );

  const status = useMemo(() => {
    if (isDoneAnyDownload) {
      return 'done';
    }
    if (itemsCount > 0) {
      return 'preparing';
    }
    return 'ready';
  }, [isDoneAnyDownload, itemsCount]);

  return {
    startDownloadPreparation,
    stopDownloadPreparation,
    getIsPreparingDownload,
    status,
    internalApiUrl,
  };
};
